import apiClient from '../api/apiClient';

/**
 * RBAC Management Service
 * Handles all API calls for Roles, Permissions, and Users management
 */

class RbacService {
  // ==================== ROLES ====================

  /**
   * Get all roles
   * @returns {Promise<{success: boolean, data: Array}>}
   */
  async getRoles(params = {}) {
    const response = await apiClient.get('/api/v1/admin/roles', { params });
    return response.data;
  }

  /**
   * Get hierarchy levels
   * @returns {Promise<{success: boolean, data: Array<{id, level, name, description}>}>}
   */
  async getHierarchyLevels() {
    const response = await apiClient.get('/api/v1/admin/hierarchy-levels');
    return response.data;
  }

  /**
   * Create a new hierarchy level
   * @param {Object} data - { level, name, description }
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async createLevel(data) {
    const response = await apiClient.post('/api/v1/admin/hierarchy-levels', data);
    return response.data;
  }

  /**
   * Update a hierarchy level
   * @param {number|string} id - Level ID
   * @param {Object} data - { name, description, is_active }
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async updateLevel(id, data) {
    const response = await apiClient.patch(`/api/v1/admin/hierarchy-levels/${id}`, data);
    return response.data;
  }

  /**
   * Delete a hierarchy level
   * @param {number|string} id - Level ID
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async deleteLevel(id) {
    const response = await apiClient.delete(`/api/v1/admin/hierarchy-levels/${id}`);
    return response.data;
  }

  /**
   * Create a new role
   * @param {Object} roleData - { name, description, level }
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async createRole(roleData) {
    const response = await apiClient.post('/api/v1/admin/roles', roleData);
    return response.data;
  }

  /**
   * Update role description
   * @param {string|number} roleId - Role ID
   * @param {Object} roleData - { description }
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async updateRole(roleId, roleData) {
    const response = await apiClient.put(`/api/v1/admin/roles/${roleId}`, roleData);
    return response.data;
  }

  /**
   * Delete a role
   * @param {string|number} roleId - Role ID
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async deleteRole(roleId) {
    const response = await apiClient.delete(`/api/v1/admin/roles/${roleId}`);
    return response.data;
  }

  /**
   * Get permissions assigned to a role
   * @param {string|number} roleId - Role ID
   * @returns {Promise<{success: boolean, data: Array}>}
   */
  async getRolePermissions(roleId) {
    const response = await apiClient.get(`/api/v1/admin/roles/${roleId}/permissions`);
    return response.data;
  }

  /**
   * Assign permissions to a role
   * @param {string|number} roleId - Role ID
   * @param {Object} data - { permission_ids: [1,2,3] }
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async assignPermissionsToRole(roleId, data) {
    const response = await apiClient.put(`/api/v1/admin/roles/${roleId}/permissions`, data);
    return response.data;
  }

  // ==================== PERMISSIONS ====================

  /**
   * Get all permissions
   * @returns {Promise<{success: boolean, data: Array}>}
   */
  async getPermissions() {
    const response = await apiClient.get('/api/v1/admin/permissions');
    return response.data;
  }

  /**
   * Create a new permission
   * @param {Object} permissionData - { key, description }
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async createPermission(permissionData) {
    const response = await apiClient.post('/api/v1/admin/permissions', permissionData);
    return response.data;
  }

  /**
   * Update a permission
   * @param {string|number} permissionId - Permission ID
   * @param {Object} permissionData - { key, description }
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async updatePermission(permissionId, permissionData) {
    const response = await apiClient.patch(`/api/v1/admin/permissions/${permissionId}`, permissionData);
    return response.data;
  }

  /**
   * Delete a permission
   * @param {string|number} permissionId - Permission ID
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async deletePermission(permissionId) {
    const response = await apiClient.delete(`/api/v1/admin/permissions/${permissionId}`);
    return response.data;
  }

  // ==================== USERS ====================

  /**
   * Get entity types
   * @returns {Promise<{success: boolean, data: Array<{value, label}>}>}
   */
  async getEntityTypes() {
    const response = await apiClient.get('/api/v1/admin/entity-types');
    return response.data;
  }

  /**
   * Get eligible managers by role level (rank-based picker).
   * @param {number} subRoleLevel - Role level; managers with level < subRoleLevel are returned
   * @param {number|string} excludeUserId - User ID to exclude from the list (self)
   * @param {string} entityTypeId - Optional entity type filter
   */
  async getManagers(subRoleLevel, excludeUserId = null, entityTypeId = null) {
    const response = await apiClient.get('/api/v1/admin/users/managers', {
      params: {
        sub_role_level: subRoleLevel,
        exclude_user_id: excludeUserId,
        ...(entityTypeId ? { entity_type_id: entityTypeId } : {}),
      },
    });
    return response.data;
  }

  /**
   * Get all potential managers for filtering
   * @returns {Promise<{success: boolean, data: Array}>}
   */
  async getAllManagers() {
    const response = await apiClient.get('/api/v1/admin/users/all-managers');
    return response.data;
  }

  /**
   * Create a new user
   * @param {Object} userData - { name, email, mobile, entity_type, role_id, manager_id }
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async createUser(userData) {
    const response = await apiClient.post('/api/v1/admin/users', userData);
    return response.data;
  }

  /**
   * Update a user
   * @param {string|number} userId - User ID
   * @param {Object} userData - { name, email, mobile, entity_type, is_active, manager_id, role_id }
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async updateUser(userId, userData) {
    const response = await apiClient.patch(`/api/v1/admin/users/${userId}`, userData);
    return response.data;
  }

  /**
   * Get roles assigned to a user
   * @param {string|number} userId - User ID
   * @returns {Promise<{success: boolean, data: Array<{id, name, description}>}>}
   */
  async getUserRoles(userId) {
    const response = await apiClient.get(`/api/v1/admin/users/${userId}/roles`);
    return response.data;
  }

  /**
   * Assign roles to a user
   * @param {string|number} userId - User ID
   * @param {Object} data - { role_ids: [1,2,3] }
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async assignRolesToUser(userId, data) {
    const response = await apiClient.put(`/api/v1/admin/users/${userId}/roles`, data);
    return response.data;
  }

  /**
   * Get effective permissions for a user
   * @param {string|number} userId - User ID
   * @returns {Promise<{success: boolean, data: Array}>}
   */
  async getUserPermissions(userId) {
    const response = await apiClient.get(`/api/v1/admin/users/${userId}/permissions`);
    return response.data;
  }

  /**
   * Get all users (if endpoint exists)
   * @returns {Promise<{success: boolean, data: Array}>}
   */
  /**
   * Get all users with optional filters
   * @param {Object} params - Filter parameters (manager_id, role_id, entity_type_id, etc.)
   * @returns {Promise<{success: boolean, data: Array}>}
   */
  async getUsers(params = {}) {
    const response = await apiClient.get('/api/v1/admin/users', { params });
    return response.data;
  }

  /**
   * Get internal organization chart for the logged-in user.
   * User ID is extracted from auth cookie on the server.
   * @returns {Promise<{success: boolean, data: Array}>}
   */
  async getInternalOrg() {
    const response = await apiClient.get('/api/v1/admin/internal-org');
    return response.data;
  }
}

const rbacService = new RbacService();

// Expose apiClient for components that need direct access (if needed)
rbacService.apiClient = apiClient;

export default rbacService;